import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { getRoutePermissions } from '@/config/routePermissions';
import { PermissionKey } from '@/lib/utils/permissions';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: PermissionKey | PermissionKey[];
}

/**
 * Route wrapper that enforces permission-based access control
 * 
 * - Admins, editors, and managers have access to everything
 * - User role must have the specified permission(s)
 * - Readonly role has view access to everything
 * 
 * @param children - The component to render if permission check passes
 * @param requiredPermission - The permission(s) required to access this route
 *   - Single permission: user must have this specific permission
 *   - Array of permissions: user must have ANY of these permissions
 */
export function PermissionProtectedRoute({ 
  children, 
  requiredPermission 
}: PermissionProtectedRouteProps) {
  const location = useLocation();
  const { hasPermission, hasAnyPermission, isAdmin, isLoading, userPerms } = usePermissions();
  
  // Wait for permissions to fully load before making any access decisions
  // Check BOTH isLoading flag AND that userPerms has been set
  // This prevents race conditions where isLoading becomes false before userPerms is set
  if (isLoading || userPerms === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }
  
  // Admins have access to everything
  // This check is safe now because we know userPerms has been loaded
  if (isAdmin()) {
    return <>{children}</>;
  }
  
  // If no specific permission required, allow access
  if (!requiredPermission) {
    return <>{children}</>;
  }
  
  // Check permission(s)
  const hasAccess = Array.isArray(requiredPermission)
    ? hasAnyPermission(requiredPermission)
    : hasPermission(requiredPermission);
  
  if (!hasAccess) {
    // Redirect to unauthorized page, preserving the attempted location
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
}
